import React, { useState } from 'react';
import { FileText, Phone, Server, CheckCircle, TrendingUp, Building2, Zap, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { BACKEND_URL } from '../config';
import axios from 'axios';

const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('payment') === 'success';

  const handleBookingClick = (serviceName) => {
    setSelectedService(serviceName);
    setIsBookingModalOpen(true);
  };

  const handleStripeCheckout = async (serviceKey) => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/services/checkout`, {
        service: serviceKey,
        originUrl: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.url) window.location.href = response.data.url;
    } catch (err) {
      if (err.response?.status === 401) window.location.href = '/login';
      else console.error('Checkout error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const services = [
    {
      icon: <FileText className="w-12 h-12" />,
      name: "Monthly Market Insights Package",
      tagline: "Stay ahead with expert market analysis",
      targetAudience: "SME Business Owners with Cash Reserves",
      description: "Once per month, receive a short, clear report (PDF) that cuts through the noise and delivers actionable insights.",
      features: [
        "Key macro economic trends analysis",
        "Sector-specific opportunities and risks",
        "Clear, jargon-free language",
        "Actionable recommendations",
        "Market outlook for the coming month"
      ],
      price: "\u20AC49.99",
      priceUnit: "per month",
      ctaText: "Subscribe Now",
      stripeKey: "market_insights",
      featured: true,
      color: "from-blue-600 to-blue-800",
      badge: "Most Popular"
    },
    {
      icon: <Phone className="w-12 h-12" />,
      name: "Reserve Opportunity Scan",
      tagline: "Discover hidden potential in your cash reserves",
      targetAudience: "Business Owners & Financial Decision Makers",
      description: "A free, no-obligation consultation to help you understand how your cash reserves could work harder for you.",
      features: [
        "30-minute personalized consultation call",
        "Professional 2-page summary report",
        "Analysis of your current reserve strategy",
        "Overview of market opportunities (no advice given)",
        "Clear next steps and options"
      ],
      price: "FREE",
      priceUnit: "one-time offer",
      ctaText: "Currently Full",
      stripeKey: null,
      featured: false,
      color: "from-green-600 to-green-800",
      badge: "Lead Generator",
      disabled: true
    },
    {
      icon: <Server className="w-12 h-12" />,
      name: "VPS + Market Info Bundle",
      tagline: "Complete solution for active traders & companies",
      targetAudience: "Active Traders & Trading Companies",
      description: "Combine professional infrastructure with market intelligence for a competitive edge in trading operations.",
      features: [
        "High-performance VPS via IOZoom partner",
        "Monthly market summary and insights",
        "99.9% uptime guarantee",
        "Priority technical support",
        "Dedicated trading infrastructure",
        "Real-time market updates"
      ],
      price: "Contact Us",
      priceUnit: "custom pricing",
      ctaText: "Request Bundle Info",
      stripeKey: null,
      featured: false,
      color: "from-purple-600 to-purple-800",
      badge: "Enterprise"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="bg-green-50 border-b border-green-200 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Payment Successful!</p>
              <p className="text-sm text-green-600">Thank you for subscribing. You'll receive your first report soon.</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Our Services
            </h1>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              Smart financial solutions designed to help your business thrive. Choose the package that fits your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Service Offerings
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent pricing, clear value. Select the service that matches your business goals.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            {services.map((service, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                  service.featured ? 'ring-4 ring-[#d97841] scale-105' : ''
                }`}
              >
                {service.badge && (
                  <div className={`absolute top-6 right-6 px-4 py-2 bg-gradient-to-r ${service.color} text-white text-sm font-bold rounded-full shadow-lg z-10`}>
                    {service.badge}
                  </div>
                )}

                <div className={`bg-gradient-to-r ${service.color} p-8 text-white`}>
                  <div className="flex justify-center mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-center mb-2">{service.name}</h3>
                  <p className="text-white/90 text-center text-sm">{service.tagline}</p>
                </div>

                <div className="p-8">
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-[#0d6571]" />
                      <span className="font-semibold text-gray-900">Who it's for:</span>
                    </div>
                    <p className="text-gray-700 text-sm">{service.targetAudience}</p>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#d97841]" /> What you get:
                    </h4>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t border-gray-200 pt-6 mb-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-900 mb-1">{service.price}</div>
                      <div className="text-sm text-gray-600">{service.priceUnit}</div>
                    </div>
                  </div>

                  <button
                    data-testid={`service-cta-${index}`}
                    onClick={() => !service.disabled && (service.stripeKey ? handleStripeCheckout(service.stripeKey) : handleBookingClick(service.name))}
                    disabled={service.disabled || (service.stripeKey && checkoutLoading)}
                    className={`w-full px-6 py-4 bg-gradient-to-r ${service.disabled ? 'from-gray-400 to-gray-500 cursor-not-allowed' : service.color} text-white rounded-full font-bold hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2 disabled:opacity-70`}
                  >
                    {service.stripeKey && checkoutLoading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to checkout...</>
                    ) : (
                      service.ctaText
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Why Choose Us Section */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose FXC Management Services?</h2>
              <p className="text-lg text-gray-600">We combine market expertise with actionable insights to help you make smarter financial decisions.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4"><TrendingUp className="w-8 h-8 text-blue-600" /></div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Analysis</h3>
                <p className="text-gray-600">Our team monitors global markets daily to deliver insights you can trust and act upon.</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4"><Building2 className="w-8 h-8 text-green-600" /></div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">SME Focused</h3>
                <p className="text-gray-600">We understand the unique challenges of SME business owners and tailor our services accordingly.</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4"><Zap className="w-8 h-8 text-purple-600" /></div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Clear & Actionable</h3>
                <p className="text-gray-600">No jargon, no confusion. Just clear insights that help you make better decisions faster.</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#0d6571] to-[#0d6571]/80 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Book a free intro call to discuss which service is right for your business. No obligations, just a friendly conversation about your needs.</p>
            <button onClick={() => handleBookingClick('Call Me Back')} className="px-8 py-4 bg-white text-[#0d6571] rounded-full font-bold hover:bg-gray-100 transition-colors text-lg">
              Call Me Back
            </button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} serviceType={selectedService} />
      <Footer />
    </div>
  );
};

export default ServicesPage;
